// Continuous Improvement Automator UI (Polished)
// Additions: threshold histogram, KB health gauge, prompt version comparison,
// dead-letter queue with retry actions, summary strip.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, Tooltip, IconButton,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, SimpleBarChart, DonutChart, RadialGauge,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    ciAutoRules, ciThresholdTune, ciPromptVersions, ciKbCurator, ciSelfImproveReport, ciDeadLetters,
} from './api';

const TABS = ['Auto-Rule Generator', 'Threshold Tuner', 'Prompt Versions', 'KB Curator', 'Self-Improvement Report', 'Dead Letter Queue'];

export default function ContinuousImprovementAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [rules, setRules] = useState(null);
    const [threshold, setThreshold] = useState(null);
    const [prompts, setPrompts] = useState(null);
    const [kbHealth, setKbHealth] = useState(null);
    const [report, setReport] = useState(null);
    const [deadLetters, setDeadLetters] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !rules) { const r = await run(() => ciAutoRules()); if (r?.data?.success) setRules(r.data.data); }
            else if (tab === 1 && !threshold) { const r = await run(() => ciThresholdTune()); if (r?.data?.success) setThreshold(r.data.data); }
            else if (tab === 2 && !prompts) { const r = await run(() => ciPromptVersions()); if (r?.data?.success) setPrompts(r.data.data); }
            else if (tab === 3 && !kbHealth) { const r = await run(() => ciKbCurator()); if (r?.data?.success) setKbHealth(r.data.data); }
            else if (tab === 4 && !report) { const r = await run(() => ciSelfImproveReport()); if (r?.data?.success) setReport(r.data.data); }
            else if (tab === 5 && !deadLetters) { const r = await run(() => ciDeadLetters()); if (r?.data?.success) setDeadLetters(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Auto-Rule Candidates', value: rules?.total_candidates ?? '—', accent: 'warning' },
        { label: 'Prompt Versions', value: prompts?.versions?.length ?? '—', accent: 'primary' },
        { label: 'KB Entries', value: kbHealth?.total_entries ?? '—', accent: 'info' },
        { label: 'Failed Runs', value: deadLetters?.total_failed ?? '—', accent: (deadLetters?.total_failed || 0) > 0 ? 'danger' : 'success' },
    ];

    const deadLetterReasonData = deadLetters?.by_reason
        ? Object.entries(deadLetters.by_reason).map(([name, value]) => ({ name, value }))
        : [];

    const renderTab = () => {
        switch (tab) {
            case 0:
                return rules ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Auto-Rule Generator" subtitle="Proposed categorization rules from consistent patterns" />
                        <SummaryGrid cards={[
                            { label: 'Candidate Rules', value: rules.total_candidates, accent: 'warning' },
                        ]} cols={1} />
                        <AIAnalysisBox text={rules.ai_assessment} title="AI Safety Assessment" />
                        <DrillTable
                            columns={[
                                { key: 'original', label: 'Original', render: r => r.original?.substring(0, 80) },
                                { key: 'corrected', label: 'Corrected To', render: r => r.corrected?.substring(0, 80) },
                                { key: 'count', label: 'Count', align: 'right', render: r => <Chip size="small" label={r.count} /> },
                                { key: 'correction_type', label: 'Type' },
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Deploy</Button> },
                            ]}
                            rows={rules.rules || []}
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 1:
                return threshold ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Confidence Threshold Tuner" subtitle="Dynamically adjust auto-execute threshold" />
                        <SummaryGrid cards={[
                            { label: 'Current Threshold', value: threshold.current_auto_threshold, accent: 'primary' },
                            { label: 'Suggested', value: threshold.suggested_auto_threshold, accent: threshold.threshold_change !== 0 ? 'info' : 'neutral' },
                            { label: 'Escalation Rate', value: `${threshold.current_escalation_rate_pct}%`, accent: 'warning' },
                        ]} cols={3} />
                        {threshold.recommendation && (
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(14,165,233,0.05)', borderLeft: '3px solid', borderColor: 'info.main' }}>
                                <Typography variant="body2">{threshold.recommendation}</Typography>
                            </Paper>
                        )}
                        {(threshold.confidence_accuracy_breakdown || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Accuracy by Confidence Bucket</Typography>
                                <SimpleBarChart
                                    data={threshold.confidence_accuracy_breakdown.map(a => ({
                                        bucket: a.confidence_bucket,
                                        accuracy: a.accuracy_pct,
                                    }))}
                                    xKey="bucket"
                                    series={[{ key: 'accuracy', label: 'Accuracy %', color: CHART_COLORS.primary }]}
                                    height={240}
                                    yFormatter={v => `${v}%`}
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'confidence_bucket', label: 'Bucket' },
                                { key: 'total', label: 'Total', align: 'right' },
                                { key: 'corrected', label: 'Corrected', align: 'right' },
                                { key: 'accuracy_pct', label: 'Accuracy', align: 'right', render: r => (
                                    <Chip size="small" color={r.accuracy_pct >= 95 ? 'success' : r.accuracy_pct >= 85 ? 'warning' : 'error'} label={`${r.accuracy_pct}%`} />
                                )},
                            ]}
                            rows={threshold.confidence_accuracy_breakdown || []}
                            rowKeyField="confidence_bucket"
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 2:
                return prompts ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Prompt Version Manager" subtitle="Compare versions across runs, confidence, length" />
                        <DrillTable
                            columns={[
                                { key: 'name', label: 'Name' },
                                { key: 'version', label: 'Version', render: r => `v${r.version}` },
                                { key: 'model', label: 'Model' },
                                { key: 'status', label: 'Status', render: r => <Chip size="small" label={r.status} /> },
                                { key: 'runs_30d', label: 'Runs 30d', align: 'right' },
                                { key: 'avg_confidence', label: 'Avg Conf', align: 'right' },
                                { key: 'prompt_length', label: 'Length', align: 'right', render: r => `${r.prompt_length} ch` },
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Diff</Button> },
                            ]}
                            rows={prompts.versions || []}
                            rowKeyField="id"
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 3:
                return kbHealth ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Knowledge Base Curator" subtitle="Maintain and optimize business knowledge base" />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>KB Health</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <RadialGauge
                                            value={kbHealth.health === 'GOOD' ? 90 : kbHealth.health === 'FAIR' ? 60 : 30}
                                            label={kbHealth.health}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <SummaryGrid cards={[
                                    { label: 'Total Entries', value: kbHealth.total_entries, accent: 'primary' },
                                    { label: 'Stale Entries', value: kbHealth.stale_entries, accent: kbHealth.stale_entries > 0 ? 'warning' : 'success' },
                                    { label: 'Duplicate Groups', value: kbHealth.duplicate_groups, accent: kbHealth.duplicate_groups > 0 ? 'warning' : 'success' },
                                ]} cols={3} />
                            </Grid>
                        </Grid>
                        {(kbHealth.stale_items || []).length > 0 && (
                            <>
                                <Typography variant="caption" fontWeight={600}>Stale Entries (6+ months)</Typography>
                                <DrillTable
                                    columns={[
                                        { key: 'category', label: 'Category' },
                                        { key: 'key', label: 'Key' },
                                        { key: 'age_days', label: 'Age (days)', align: 'right' },
                                        { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Archive</Button> },
                                    ]}
                                    rows={kbHealth.stale_items}
                                    rowKeyField="id"
                                    maxHeight={320}
                                />
                            </>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 4:
                return report ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Self-Improvement Report" subtitle="Agent learning trajectory" />
                        <AIAnalysisBox text={report.ai_narrative} title="AI Self-Improvement Report" />
                        <SummaryGrid cards={[
                            { label: 'Learning Trend', value: report.metrics?.correction_rate_trend || '—', accent: report.metrics?.correction_rate_trend === 'IMPROVING' ? 'success' : 'warning' },
                            { label: 'Override Rate', value: report.metrics?.override_rate != null ? `${report.metrics.override_rate}%` : '—', accent: 'warning' },
                            { label: '30d Cost', value: report.metrics?.total_cost_30d != null ? `$${report.metrics.total_cost_30d.toFixed(2)}` : '—', accent: 'info' },
                        ]} cols={3} />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 5:
                return deadLetters ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Dead Letter Queue" subtitle="Failed runs — investigate and retry" />
                        <SummaryGrid cards={[
                            { label: 'Failed Runs', value: deadLetters.total_failed, accent: deadLetters.total_failed > 0 ? 'danger' : 'success' },
                            { label: 'Stuck Approvals', value: deadLetters.stuck_approvals || 0, accent: deadLetters.stuck_approvals > 0 ? 'warning' : 'success' },
                            { label: 'Top Action', value: deadLetters.top_action || '—', accent: 'info' },
                        ]} cols={3} />
                        {deadLetterReasonData.length > 0 && (
                            <Grid container spacing={1}>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Failures by Reason</Typography>
                                        <DonutChart data={deadLetterReasonData} height={220} />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Counts</Typography>
                                        <SimpleBarChart
                                            data={deadLetterReasonData}
                                            xKey="name"
                                            series={[{ key: 'value', label: 'Count', color: CHART_COLORS.danger }]}
                                            height={220}
                                            showLegend={false}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        {(deadLetters.recent_failures || []).length > 0 && (
                            <DrillTable
                                columns={[
                                    { key: 'run_id', label: 'Run' },
                                    { key: 'task', label: 'Task', render: r => r.task?.substring(0, 80) },
                                    { key: 'reason', label: 'Reason', render: r => <Chip size="small" label={r.reason} /> },
                                    { key: 'created_at', label: 'Date', render: r => r.created_at?.substring(0, 10) },
                                    { key: 'action', label: '', render: () => (
                                        <Tooltip title="Retry">
                                            <IconButton size="small" color="primary"><ReplayIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    )},
                                ]}
                                rows={deadLetters.recent_failures.slice(0, 20)}
                                rowKeyField="run_id"
                                maxHeight={420}
                            />
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Continuous Improvement Automator"
            subtitle="Agentic Learning — auto-rules, threshold tuning, prompt versions, KB curator, dead letter queue."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
